import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
type Region = {
  code: string;
  // ISO 3166-1 alpha-2, lowercased, used for the flag image.
  iso: string;
  name: string;
  available: boolean;
};
// Talab launches in the UAE first; other GCC markets follow.
const REGIONS: Region[] = [{
  code: 'UAE',
  iso: 'ae',
  name: 'United Arab Emirates',
  available: true
}, {
  code: 'KSA',
  iso: 'sa',
  name: 'Saudi Arabia',
  available: false
}, {
  code: 'QAT',
  iso: 'qa',
  name: 'Qatar',
  available: false
}, {
  code: 'KWT',
  iso: 'kw',
  name: 'Kuwait',
  available: false
}, {
  code: 'BHR',
  iso: 'bh',
  name: 'Bahrain',
  available: false
}, {
  code: 'OMN',
  iso: 'om',
  name: 'Oman',
  available: false
}];
/**
 * The flag in the header trigger. A full-strength flag is the most saturated
 * thing in the header, so at rest it's a small circle with its colour pulled
 * well back and a soft white veil — still recognisable, no longer shouting.
 * Hovering or opening the selector brings its true colour back smoothly,
 * which is exactly when the user is actually looking at it.
 */
function QuietFlag({
  iso,
  active
}: {
  iso: string;
  active: boolean;
}) {
  return (
    // Height is fixed and width follows the flag's own proportions, so the
    // whole flag always shows — nothing is cropped, whatever its shape.
    <span className="relative inline-flex flex-shrink-0 h-[14px] rounded-[3px] overflow-hidden ring-1 ring-primary-indigo/15" data-id="element-3210" data-mp="3aiwtzcmyrgzg">
      <img src={`https://flagcdn.com/h20/${iso}.png`} srcSet={`https://flagcdn.com/h40/${iso}.png 2x`} height={14} alt="" aria-hidden="true" className={`block h-[14px] w-auto max-w-none transition-[filter,opacity] duration-300 ease-out group-hover/region:saturate-100 group-hover/region:opacity-100 ${active ? 'saturate-100 opacity-100' : 'saturate-[0.35] opacity-80'}`} data-id="element-3211" data-mp="35qvme40qqefr" />
      <span aria-hidden className={`absolute inset-0 bg-white transition-opacity duration-300 ease-out group-hover/region:opacity-0 ${active ? 'opacity-0' : 'opacity-25'}`} data-id="element-3212" data-mp="1rmuwcikvlbz3" />
    </span>
  );
}
function Flag({
  iso,
  className = ''
}: {
  iso: string;
  className?: string;
}) {
  return <img src={`https://flagcdn.com/w40/${iso}.png`} srcSet={`https://flagcdn.com/w80/${iso}.png 2x`} width={20} height={15} alt="" aria-hidden="true" className={`rounded-[3px] object-cover shadow-sm ring-1 ring-black/5 ${className}`} data-id="element-3213" data-mp="1mrfkfw6fhq5b" />;
}
export function RegionSelector({
  compact = false
}: {
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<Region>(REGIONS[0]);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const choose = (r: Region) => {
    if (!r.available) return;
    setActive(r);
    setOpen(false);
  };
  return <div className="relative" ref={ref} data-id="element-3214" data-mp="rkyhujdrq8ul">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={`Region: ${active.name}`}
    // Same material as Log in / Sign up: a hairline indigo→purple border
    // drifting slowly around a white pill, with a whisper of tint inside.
    className="group/region relative isolate inline-flex p-px rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-indigo/30 focus-visible:ring-offset-2" data-id="element-3215" data-mp="rjwtxebnlrvg">
        <span aria-hidden className={`ask-ai-drift absolute inset-0 -z-10 rounded-full bg-gradient-to-r from-primary-indigo/25 via-ai-purple/35 to-primary-indigo/25 bg-[length:300%_100%] transition-opacity duration-200 ease-out ${open ? 'opacity-100' : 'opacity-80 group-hover/region:opacity-100'}`} data-id="element-3216" data-mp="3c2j6s87quggz" />
        <span className={`relative flex items-center gap-2 overflow-hidden rounded-full bg-white ${compact ? 'px-2.5 py-1.5' : 'px-3 py-2'}`} data-id="element-3217" data-mp="2d3ine6qsyrxn">
          <span aria-hidden className={`absolute inset-0 bg-gradient-to-r transition-colors duration-200 ease-out ${open ? 'from-primary-indigo/[0.09] via-ai-purple/[0.12] to-primary-indigo/[0.09]' : 'from-primary-indigo/[0.05] via-ai-purple/[0.07] to-primary-indigo/[0.05] group-hover/region:from-primary-indigo/[0.09] group-hover/region:via-ai-purple/[0.12] group-hover/region:to-primary-indigo/[0.09]'}`} data-id="element-3218" data-mp="2h317x1epp0zy" />
          <span className="relative flex" data-id="element-3219" data-mp="11nk9npkojhq3">
            <QuietFlag iso={active.iso} active={open} data-id="element-3220" data-mp="11miwnfk3sl5q" />
          </span>
          {!compact && <span className="relative text-sm font-semibold text-primary-indigo tracking-wide leading-5" data-id="element-3221" data-mp="15o44unlp0b9l">
              {active.code}
            </span>}
          <ChevronDownIcon className={`relative w-4 h-4 text-primary-indigo/60 transition-transform duration-200 ease-out ${open ? 'rotate-180' : ''}`} data-id="element-3222" data-mp="t8wdboigbqsk" />
        </span>
      </button>

      <AnimatePresence data-id="element-3223" data-mp="k64z5gy6ygx8">
        {open && <motion.div role="listbox" initial={{
        opacity: 0,
        y: -8,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -8,
        scale: 0.97
      }} transition={{
        duration: 0.16,
        ease: [0.16, 1, 0.3, 1]
      }} className="absolute right-0 mt-2.5 w-72 bg-white border border-slate-100 rounded-2xl shadow-[0_16px_48px_-8px_rgb(0,0,0,0.18)] overflow-hidden z-50" data-id="element-3224" data-mp="f7tqh9jmucgl">
            {/* A hairline of the same indigo→purple at the top ties the
                panel to its trigger without adding colour to the list. */}
            <div aria-hidden className="h-px bg-gradient-to-r from-primary-indigo/0 via-ai-purple/40 to-primary-indigo/0" data-id="element-3225" data-mp="3423g3w7oidvj" />
            <div className="px-4 pt-3.5 pb-2" data-id="element-3226" data-mp="3930vk11b0ttc">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400" data-id="element-3227" data-mp="kuztgslnm2x">
                Your region
              </p>
            </div>
            <div className="px-2 pb-2 space-y-0.5" data-id="element-3228" data-mp="5maoditzkaq2">
              {REGIONS.map(r => {
            const isActive = r.code === active.code;
            return <button key={r.code} type="button" role="option" aria-selected={isActive} onClick={() => choose(r)} disabled={!r.available} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${isActive ? 'bg-primary-indigo/[0.06]' : r.available ? 'hover:bg-slate-50' : 'cursor-not-allowed'}`} data-id="element-3229" data-mp="3ry7ufdo8x6d9">
                    <Flag iso={r.iso} className={`w-7 h-[21px] ${!r.available ? 'opacity-50 saturate-[0.4]' : ''}`} data-id="element-3230" data-mp="2iqebvpgfdngn" />
                    <span className="flex-1 min-w-0" data-id="element-3231" data-mp="1q3bdcxbr1cqe">
                      <span className={`block text-sm truncate ${isActive ? 'font-semibold text-primary-indigo' : r.available ? 'font-medium text-slate-800' : 'font-medium text-slate-400'}`} data-id="element-3232" data-mp="1l35jdai0be55">
                        {r.name}
                      </span>
                      {!r.available && <span className="block text-[11px] text-slate-400 mt-0.5" data-id="element-3233" data-mp="3ah26sjri9wlv">
                          Coming soon
                        </span>}
                    </span>
                    {isActive ? <span className="w-5 h-5 rounded-full bg-primary-indigo flex items-center justify-center shrink-0" data-id="element-3234" data-mp="13rqkce6q1a42">
                        <CheckIcon className="w-3 h-3 text-white" data-id="element-3235" data-mp="1dsaobolmkspz" />
                      </span> : r.available ? <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-id="element-3236" data-mp="1ijsmy606eh7s">
                        {r.code}
                      </span> : null}
                  </button>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}